import { StepUpConfigResponse, VerifyOptions } from '@/api/step-up';
import {
  AdditionalValidations,
  AgreementResponse,
  IdentifierType,
  ValidatedAgreements,
} from '@/api/types';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type StepUpState = StepUpConfigResponse & {
  initialized: boolean;
  identifier: string;
  identifierType?: IdentifierType;
  maxAttemptsReachedList: VerifyOptions[];
  validatedAgreements: ValidatedAgreements[];
  additionalValidations?: AdditionalValidations;
};

export const defaultStepUpState: StepUpState = {
  signedInEmail: '',
  allowedIdentifiers: [IdentifierType.PHONE],
  carrierPinOptions: {
    isAllowed: false,
    encryptionRequired: false,
  },
  initialized: false,
  maxAttemptsReachedList: [],
  identifier: '',
  validatedAgreements: [],
};

export type StepUpStore = StepUpState & {
  setConfig: (config: StepUpConfigResponse) => void;
  updateVerificationResult: (
    result: AgreementResponse,
    mergeAgreements?: boolean,
  ) => void;
  updateMaxAttemptsReachedList: (option: VerifyOptions) => void;
  updateIdentifier: (
    identifier: string,
    identifierType?: IdentifierType,
  ) => void;
};

export const useStepUpStore = create(
  persist<StepUpStore>(
    (set) => ({
      ...defaultStepUpState,
      updateMaxAttemptsReachedList: (option: VerifyOptions) => {
        set((state) => ({
          maxAttemptsReachedList: [...state.maxAttemptsReachedList, option],
        }));
      },
      validatedAgreements: [],
      setConfig: (config) => {
        set({
          initialized: true,
          ...config,
        });
      },
      updateIdentifier: (
        identifier: string,
        identifierType?: IdentifierType,
      ) => {
        set((s) => ({
          identifier,
          identifierType,
          maxAttemptsReachedList:
            s.identifier !== identifier ? [] : s.maxAttemptsReachedList,
        }));
      },
      updateVerificationResult: (
        result: AgreementResponse,
        mergeAgreements?: boolean,
      ) => {
        set((s) => ({
          validatedAgreements: mergeAgreements
            ? s.validatedAgreements.concat(result.validatedAgreements ?? [])
            : (result.validatedAgreements ?? []),
          additionalValidations: result.additionalValidations,
        }));
      },
    }),
    {
      name: 'step-up-store',
      storage: createJSONStorage(() => sessionStorage),
      //only hydrate when rendering to avoid hydration errors
      skipHydration: true,
    },
  ),
);
